import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import moment from 'moment';
import * as XLSX from 'xlsx';
import BeneficiaryValidationApi from './beneficiaryValidationApi';
import ControlPanel from './controlPanel';
import SummaryStrip from './summaryStrip';
import ColumnWarnings from './columnWarnings';
import ValidationGrid from './validationGrid';
import PrintView from './printView';
import {
  acceptFile,
  columnsFor,
  errorMessage,
  exportFileName,
  filterRows,
  isBusyMessage,
  isIntlFormat,
  isWrongFormatMessage,
  mismatchLabel,
} from './beneficiaryValidationHelpers';
import './beneficiaryValidation.css';

const BeneficiaryListValidation = () => {
  const api = useMemo(() => new BeneficiaryValidationApi(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const bankFormat = searchParams.get('bankFormat') || '';

  const [formats, setFormats] = useState([]);
  const [file, setFile] = useState(null);
  const [validation, setValidation] = useState(null);
  const [importInfo, setImportInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyText, setBusyText] = useState('');
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [mismatchesOnly, setMismatchesOnly] = useState(false);
  const [highlightAmount, setHighlightAmount] = useState(false);
  // Ignores responses from a format the user has already moved away from.
  const requestId = useRef(0);

  const intl = isIntlFormat(bankFormat);
  const sheetName = formats.find((f) => f.value === bankFormat)?.sheetName || '';

  const loadValidation = useCallback(
    async (format) => {
      const id = ++requestId.current;
      if (!format) return;
      try {
        setBusy(true);
        setBusyText('Loading…');
        const res = await api.getValidation(format);
        if (id !== requestId.current) return;
        // 204: nothing is staged for this format - show the empty state, not an error.
        if (res.status === 204 || !res?.data?.result) {
          setValidation(null);
        } else {
          setValidation(res.data.result);
        }
      } catch (err) {
        if (id !== requestId.current) return;
        setValidation(null);
        if (err?.response?.status === 400) setError({ message: errorMessage(err) });
        else toast.error(errorMessage(err));
      } finally {
        if (id === requestId.current) setBusy(false);
      }
    },
    [api],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getBankFormats();
        if (!cancelled) setFormats(res?.data?.result || []);
      } catch (err) {
        if (!cancelled) toast.error(errorMessage(err));
      }
    })();
    if (bankFormat) loadValidation(bankFormat);
    // The staging tables are shared by all users, so clear them when leaving.
    // Fire-and-forget: never block navigation or show an error if it fails.
    return () => {
      cancelled = true;
      api.clearStaging().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearScreen = () => {
    requestId.current += 1;
    setFile(null);
    setValidation(null);
    setImportInfo(null);
    setError(null);
    setSearch('');
    setMismatchesOnly(false);
    setHighlightAmount(false);
  };

  const handleFormatChange = (format) => {
    clearScreen();
    // Only updates the screen and URL; the API is called from the Validate button.
    if (format) setSearchParams({ bankFormat: format });
    else setSearchParams({});
  };

  const handleFileChange = (chosen) => {
    setFile(chosen);
    setError(null);
  };

  const handleValidate = async () => {
    if (!bankFormat) {
      toast.warning('Select a bank format.');
      return;
    }
    const fileError = acceptFile(file);
    if (fileError) {
      toast.warning(fileError);
      return;
    }
    try {
      setError(null);
      setBusy(true);
      setBusyText('Validating, please wait…');
      const res = await api.importBeneficiaryFile(bankFormat, file);
      const result = res?.data?.result;
      // The comparison comes back in this response - no second call.
      setValidation(result?.validation || null);
      setImportInfo({
        unmappedColumns: result?.unmappedColumns || [],
        missingColumns: result?.missingColumns || [],
      });
      setSearch('');
      setMismatchesOnly(false);
      toast.success(res?.data?.message || 'File validated');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) setError({ message: errorMessage(err) });
      else toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRefresh = () => {
    setError(null);
    loadValidation(bankFormat);
  };

  const handleReset = async () => {
    if (!window.confirm('Clear the uploaded data?')) return;
    try {
      setBusy(true);
      setBusyText('Clearing…');
      await api.clearStaging();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
    clearScreen();
    setSearchParams({});
  };

  const rows = validation?.rows || [];
  const totals = validation?.totals || null;
  const visibleRows = useMemo(() => filterRows(rows, { search, mismatchesOnly }), [rows, search, mismatchesOnly]);
  const isFiltered = !!search.trim() || mismatchesOnly;
  const hasRows = rows.length > 0;

  const handleExport = () => {
    const columns = columnsFor(bankFormat);
    const header = [...columns.map((c) => c.headerName), 'Mismatch'];
    const body = visibleRows.map((row) => [...columns.map((c) => row[c.field] ?? ''), mismatchLabel(row)]);
    const aoa = [
      ['Beneficiary List Validation'],
      ['Bank Format', bankFormat],
      ['Validated By', validation?.validateBy || ''],
      ['Validated On', validation?.validatedOn ? moment(validation.validatedOn).format('DD-MMM-YYYY HH:mm') : ''],
      [],
      header,
      ...body,
    ];
    if (!intl && totals) {
      const totalRow = columns.map(() => '');
      totalRow[0] = isFiltered ? 'Total (all rows)' : 'Total';
      totalRow[columns.findIndex((c) => c.field === 'systemAmount')] = totals.totalSystemAmount;
      totalRow[columns.findIndex((c) => c.field === 'bankAmount')] = totals.totalBankAmount;
      aoa.push([...totalRow, '']);
    }
    const sheet = XLSX.utils.aoa_to_sheet(aoa);
    sheet['!cols'] = header.map((h) => ({ wch: Math.max(14, h.length + 2) }));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Validation');
    XLSX.writeFile(book, `${exportFileName(bankFormat)}.xlsx`);
  };

  const handlePrint = () => window.print();

  return (
    <div className="p-3">
      {busy ? (
        <div className="bv-overlay">
          <ClipLoader color="#6E5199" size={40} />
          <div>{busyText}</div>
        </div>
      ) : null}

      <div className="border-box2 p-4">
        <h5 className="mainInvHead">Beneficiary List Validation</h5>
        <ControlPanel
          formats={formats}
          bankFormat={bankFormat}
          onFormatChange={handleFormatChange}
          file={file}
          onFileChange={handleFileChange}
          sheetName={sheetName}
          canValidate={!!bankFormat && !!file}
          onValidate={handleValidate}
          hasRows={hasRows}
          onRefresh={handleRefresh}
          onReset={handleReset}
          onExport={handleExport}
          onPrint={handlePrint}
          disabled={busy}
        />
      </div>

      <div className="border-box2 p-4 mt-3">
        {error ? (
          <div className="bv-error mb-3">
            <div>{error.message}</div>
            {isWrongFormatMessage(error.message) ? (
              <div className="small mt-1">Check the Bank Format selection above — this is almost always the wrong bank format for the file.</div>
            ) : null}
            {isBusyMessage(error.message) ? (
              <button type="button" className="mainbtnGry mt-2" onClick={handleValidate} disabled={busy}>
                Retry
              </button>
            ) : null}
          </div>
        ) : null}

        <ColumnWarnings unmappedColumns={importInfo?.unmappedColumns} missingColumns={importInfo?.missingColumns} />

        {hasRows ? (
          <>
            <SummaryStrip
              totals={totals}
              bankFormat={bankFormat}
              mismatchesOnly={mismatchesOnly}
              onMismatchClick={() => setMismatchesOnly((v) => !v)}
            />
            <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
              <input
                type="text"
                className="form-control"
                style={{ maxWidth: 320 }}
                placeholder="Search ref, property, unit, beneficiary"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <label className="d-flex align-items-center gap-1 mb-0">
                <input type="checkbox" checked={mismatchesOnly} onChange={(e) => setMismatchesOnly(e.target.checked)} />
                Mismatches only
              </label>
              {!intl ? (
                <label className="d-flex align-items-center gap-1 mb-0">
                  <input type="checkbox" checked={highlightAmount} onChange={(e) => setHighlightAmount(e.target.checked)} />
                  Highlight amount differences
                </label>
              ) : null}
              {isFiltered ? (
                <span className="text-muted small">
                  showing {visibleRows.length} of {rows.length} rows
                </span>
              ) : null}
            </div>
            <div className="gridshad">
              <ValidationGrid rows={visibleRows} bankFormat={bankFormat} totals={totals} highlightAmount={highlightAmount} isFiltered={isFiltered} />
            </div>
          </>
        ) : (
          <div className="text-muted py-4 text-center">
            {bankFormat ? 'Nothing is staged for this bank format. Choose the bank file and press Validate.' : 'Select a bank format to begin.'}
          </div>
        )}
      </div>

      {hasRows ? (
        <PrintView
          bankFormat={bankFormat}
          validateBy={validation?.validateBy}
          validatedOn={validation?.validatedOn}
          rows={visibleRows}
          totals={totals}
          highlightAmount={highlightAmount}
          isFiltered={isFiltered}
        />
      ) : null}
    </div>
  );
};

export default BeneficiaryListValidation;
